// src/pages/Evasion.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaPlane,
  FaGlobe,
  FaBell,
  FaInfoCircle,
  FaCheckCircle,
  FaArrowRight,
  FaSuitcaseRolling,
  FaMapMarkedAlt,
  FaRegClock,
  FaSearchLocation,
  FaExternalLinkAlt,
} from 'react-icons/fa';
import { supabase } from '../supabaseClient';
import { createSmartAlert } from '../lib/smartAlerts';
import { buildKiwiFlightSearchUrl } from '../lib/kiwiAffiliate';
import Header from '../components/Header';
import Footer from '../components/Footer';

const AIRPORTS = [
  { code: 'YUL', city: 'Montréal', cityEn: 'Montreal', country: 'Canada' },
  { code: 'YQB', city: 'Québec', cityEn: 'Quebec City', country: 'Canada' },
  { code: 'YYZ', city: 'Toronto', cityEn: 'Toronto', country: 'Canada' },
  { code: 'YVR', city: 'Vancouver', cityEn: 'Vancouver', country: 'Canada' },
  { code: 'CDG', city: 'Paris (Charles de Gaulle)', cityEn: 'Paris (Charles de Gaulle)', country: 'France' },
  { code: 'ORY', city: 'Paris (Orly)', cityEn: 'Paris (Orly)', country: 'France' },
  { code: 'NCE', city: 'Nice', cityEn: 'Nice', country: 'France' },
  { code: 'LIS', city: 'Lisbonne', cityEn: 'Lisbon', country: 'Portugal' },
  { code: 'OPO', city: 'Porto', cityEn: 'Porto', country: 'Portugal' },
  { code: 'CUN', city: 'Cancun', cityEn: 'Cancun', country: 'Mexico' },
  { code: 'PUJ', city: 'Punta Cana', cityEn: 'Punta Cana', country: 'Dominican Republic' },
  { code: 'NRT', city: 'Tokyo (Narita)', cityEn: 'Tokyo (Narita)', country: 'Japan' },
  { code: 'HND', city: 'Tokyo (Haneda)', cityEn: 'Tokyo (Haneda)', country: 'Japan' },
  { code: 'LHR', city: 'Londres (Heathrow)', cityEn: 'London (Heathrow)', country: 'United Kingdom' },
  { code: 'LGW', city: 'Londres (Gatwick)', cityEn: 'London (Gatwick)', country: 'United Kingdom' },
  { code: 'FCO', city: 'Rome', cityEn: 'Rome', country: 'Italy' },
  { code: 'BCN', city: 'Barcelone', cityEn: 'Barcelona', country: 'Spain' },
  { code: 'MAD', city: 'Madrid', cityEn: 'Madrid', country: 'Spain' },
  { code: 'MIA', city: 'Miami', cityEn: 'Miami', country: 'United States' },
  { code: 'JFK', city: 'New York (JFK)', cityEn: 'New York (JFK)', country: 'United States' },
  { code: 'LAX', city: 'Los Angeles', cityEn: 'Los Angeles', country: 'United States' },
  { code: 'LAS', city: 'Las Vegas', cityEn: 'Las Vegas', country: 'United States' },
  { code: 'DXB', city: 'Dubaï', cityEn: 'Dubai', country: 'United Arab Emirates' },
];

const copy = {
  fr: {
    badge: 'Gratuit pour les voyageurs Forfeo',
    titleBefore: 'Pars quand la',
    titleAccent: 'bonne occasion',
    titleAfter: 'apparaît.',
    subtitle: 'Crée une alerte gratuite avec ton budget, tes dates et tes destinations préférées. Forfeo Évasion surveille les opportunités voyage et t’avise lorsqu’un bon départ se présente.',
    createAlert: 'Créer une alerte gratuite',
    howItWorks: 'Comment ça fonctionne',
    formTitle: 'Activer mon alerte Évasion',
    formSubtitle: 'Remplis tes critères pour ne plus jamais manquer un bon départ.',
    origin: 'Ville de départ',
    originPlaceholder: 'Ex: Montréal (YUL)',
    destination: 'Destination souhaitée',
    destinationPlaceholder: "Ex: Lisbonne ou 'Europe'",
    maxBudget: 'Budget maximum (CAD)',
    budgetPlaceholder: 'Ex: 800',
    period: 'Période ou mois',
    periodPlaceholder: 'Ex: Septembre ou Relâche',
    flexibility: 'Flexibilité',
    fixed: 'Dates précises',
    flexible: 'Flexible (+/- 3 jours)',
    veryFlexible: 'Très flexible (le mois complet)',
    passengers: 'Voyageurs',
    directOnly: 'Vol direct seulement',
    emailConsent: "J'accepte de recevoir des alertes courriel automatiques de Forfeo Évasion lorsqu'une opportunité correspond à mes critères.",
    loading: 'Activation...',
    submit: 'Activer mon alerte gratuite',
    disclaimer: 'Prix détecté au moment de l’analyse. Disponibilité et conditions sujettes à changement.',
    successTitle: 'Ton alerte est prête !',
    successText: "Ton alerte Forfeo Évasion est configurée avec le radar intelligent. Tu recevras un courriel avec un lien Kiwi affilié dès qu'une opportunité correspondra à tes critères.",
    viewAlerts: 'Voir mes alertes',
    anotherAlert: 'Créer une autre alerte',
    consentError: 'Tu dois accepter de recevoir les alertes courriel pour activer cette alerte.',
    saveError: "Impossible de sauvegarder l'alerte pour le moment. Réessaie plus tard.",
    serverError: 'Une erreur de communication avec le serveur est survenue.',
    stepsTitle: 'Comment ça fonctionne',
    step1Title: 'Choisis ton départ',
    step1Text: 'Indique ton budget, tes dates et tes destinations préférées.',
    step2Title: 'Forfeo surveille les opportunités',
    step2Text: 'Le système intelligent Forfeo analyse en continu les opportunités qui correspondent à tes critères.',
    step3Title: 'Tu reçois une alerte',
    step3Text: "Lorsqu'un bon départ apparaît, Forfeo t'avise immédiatement par courriel.",
    popular: 'Alertes populaires',
    under: 'Sous',
    planned: 'Prévu en',
    openTitle: 'Tu ne sais pas encore où partir ?',
    openText: "Forfeo Évasion peut aussi t'aider à surveiller une envie plutôt qu'une destination précise. Laisse notre technologie Forfeo trouver les perles rares pour toi.",
    indexTitle: 'Indice Évasion',
    indexSub: 'Analyse Forfeo en temps réel',
    priceScore: 'Score de prix',
    excellent: 'Excellent',
    sampleQuote: 'Nous avons détecté une baisse de 22% sur les vols vers Lisbonne pour tes dates.',
    emailPreviewTitle: "Reçois l'alerte au bon moment",
    emailPreviewText: 'Les bons prix disparaissent rapidement. Forfeo Évasion t’avise dès qu’une opportunité est repérée.',
    subject: 'Objet :',
    subjectText: 'Forfeo Évasion : Une opportunité vient d’apparaître',
    spotted: 'DESTINATION REPÉRÉE',
    detectedPrice: 'PRIX DÉTECTÉ',
    seeOffer: "Voir l'offre maintenant",
    importantTitle: 'Important à savoir',
    importantText1: 'Forfeo Évasion est un service de repérage et d’alerte basé sur la technologie Forfeo. Les prix, disponibilités, horaires, frais, bagages et conditions de réservation sont fournis par des sources ou partenaires externes et peuvent changer sans préavis. Forfeo ne vend pas directement les billets d’avion.',
    importantText2: "Le prix affiché est celui détecté au moment de l’analyse. La réservation, le paiement, les changements, annulations et conditions de transport relèvent du fournisseur externe. Certaines restrictions peuvent s'appliquer selon les transporteurs.",
    finalTitle: 'Prêt à surveiller ton prochain départ ?',
    manageAlerts: 'Gérer mes alertes',
    any: 'Peu importe',
  },
  en: {
    badge: 'Free for Forfeo travelers',
    titleBefore: 'Leave when the',
    titleAccent: 'right opportunity',
    titleAfter: 'appears.',
    subtitle: 'Create a free alert with your budget, travel period and favorite destinations. Forfeo Escape watches travel opportunities and notifies you when a good departure appears.',
    createAlert: 'Create a free alert',
    howItWorks: 'How it works',
    formTitle: 'Activate my Escape alert',
    formSubtitle: 'Enter your criteria and never miss a great departure again.',
    origin: 'Departure city',
    originPlaceholder: 'Example: Montreal (YUL)',
    destination: 'Desired destination',
    destinationPlaceholder: "Example: Lisbon or 'Europe'",
    maxBudget: 'Maximum budget (CAD)',
    budgetPlaceholder: 'Example: 800',
    period: 'Period or month',
    periodPlaceholder: 'Example: September or Spring Break',
    flexibility: 'Flexibility',
    fixed: 'Exact dates',
    flexible: 'Flexible (+/- 3 days)',
    veryFlexible: 'Very flexible (whole month)',
    passengers: 'Travelers',
    directOnly: 'Direct flights only',
    emailConsent: 'I agree to receive automatic Forfeo Escape email alerts when an opportunity matches my criteria.',
    loading: 'Activating...',
    submit: 'Activate my free alert',
    disclaimer: 'Detected prices are based on the time of analysis. Availability and conditions may change.',
    successTitle: 'Your alert is ready!',
    successText: 'Your Forfeo Escape alert is configured with the smart radar. You will receive an email with a Kiwi affiliate link whenever an opportunity matches your criteria.',
    viewAlerts: 'View my alerts',
    anotherAlert: 'Create another alert',
    consentError: 'You must agree to receive email alerts to activate this alert.',
    saveError: 'Unable to save the alert right now. Please try again later.',
    serverError: 'A communication error with the server occurred.',
    stepsTitle: 'How it works',
    step1Title: 'Choose your departure',
    step1Text: 'Enter your budget, dates and preferred destinations.',
    step2Title: 'Forfeo watches opportunities',
    step2Text: 'The Forfeo intelligent system continuously analyzes opportunities that match your criteria.',
    step3Title: 'You receive an alert',
    step3Text: 'When a good departure appears, Forfeo immediately notifies you by email.',
    popular: 'Popular alerts',
    under: 'Under',
    planned: 'Planned for',
    openTitle: 'Not sure where to go yet?',
    openText: 'Forfeo Escape can also watch a travel mood instead of a precise destination. Let Forfeo technology find hidden gems for you.',
    indexTitle: 'Escape Index',
    indexSub: 'Real-time Forfeo analysis',
    priceScore: 'Price score',
    excellent: 'Excellent',
    sampleQuote: 'We detected a 22% price drop on flights to Lisbon for your dates.',
    emailPreviewTitle: 'Receive the alert at the right moment',
    emailPreviewText: 'Good prices disappear fast. Forfeo Escape notifies you as soon as an opportunity is found.',
    subject: 'Subject:',
    subjectText: 'Forfeo Escape: A new opportunity just appeared',
    spotted: 'DESTINATION SPOTTED',
    detectedPrice: 'DETECTED PRICE',
    seeOffer: 'See the offer now',
    importantTitle: 'Important to know',
    importantText1: 'Forfeo Escape is a travel spotting and alert service powered by Forfeo technology. Prices, availability, schedules, fees, baggage rules and booking conditions come from external sources or partners and may change without notice. Forfeo does not directly sell airline tickets.',
    importantText2: 'The displayed price is the price detected at the time of analysis. Booking, payment, changes, cancellations and transport conditions are handled by the external provider. Restrictions may apply depending on the carrier.',
    finalTitle: 'Ready to watch your next departure?',
    manageAlerts: 'Manage my alerts',
    any: 'Any',
  },
};

const quickDeals = [
  { originCode: 'YUL', destCode: 'LIS', labelO: 'Montréal', labelD: 'Lisbonne', labelOEn: 'Montreal', labelDEn: 'Lisbon', budget: '750', period: 'Septembre', periodEn: 'September' },
  { originCode: 'YQB', destCode: 'CDG', labelO: 'Québec', labelD: 'Paris', labelOEn: 'Quebec City', labelDEn: 'Paris', budget: '850', period: 'Mai', periodEn: 'May' },
  { originCode: 'YUL', destCode: 'CUN', labelO: 'Montréal', labelD: 'Cancun', labelOEn: 'Montreal', labelDEn: 'Cancun', budget: '600', period: 'Février', periodEn: 'February' },
  { originCode: 'YYZ', destCode: 'NRT', labelO: 'Toronto', labelD: 'Tokyo', labelOEn: 'Toronto', labelDEn: 'Tokyo', budget: '1000', period: 'Automne', periodEn: 'Fall' },
];

const openDestinations = [
  { label: 'Soleil en février', labelEn: 'Sun in February', originCode: 'YUL', destCode: 'CUN', budget: '800', period: 'Février' },
  { label: 'Europe romantique', labelEn: 'Romantic Europe', originCode: 'YUL', destCode: 'CDG', budget: '900', period: 'Mai' },
  { label: 'Voyage famille (Relâche)', labelEn: 'Family trip (Spring Break)', originCode: 'YQB', destCode: 'PUJ', budget: '1200', period: 'Mars' },
  { label: "N'importe où (Bon prix)", labelEn: 'Anywhere (good price)', originCode: 'YUL', destCode: 'LIS', budget: '600', period: 'Flexible' },
];

const AirportSelect = ({ value, onChange, placeholder, required, isEn }: any) => {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const airport = AIRPORTS.find((a) => a.code === value);
    if (airport) {
      setSearch(`${isEn ? airport.cityEn : airport.city} (${airport.code})`);
    } else if (value) {
      setSearch(value);
    } else {
      setSearch('');
    }
  }, [value, isEn]);

  const filtered = AIRPORTS.filter(
    (a) =>
      a.city.toLowerCase().includes(search.toLowerCase()) ||
      a.cityEn.toLowerCase().includes(search.toLowerCase()) ||
      a.code.toLowerCase().includes(search.toLowerCase()) ||
      a.country.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="relative">
      <div className="relative flex items-center">
        <FaSearchLocation className="absolute left-4 text-slate-400" />
        <input
          type="text"
          required={required}
          placeholder={placeholder}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            onChange(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onBlur={() => setTimeout(() => setIsOpen(false), 200)}
          className="w-full pl-11 pr-5 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-sky-500 outline-none transition-all"
        />
      </div>

      {isOpen && filtered.length > 0 && (
        <div className="absolute z-50 w-full mt-2 bg-white border border-slate-100 rounded-2xl shadow-xl max-h-60 overflow-y-auto">
          {filtered.map((a) => (
            <button
              type="button"
              key={a.code}
              className="w-full px-5 py-3 hover:bg-sky-50 cursor-pointer flex justify-between items-center transition-colors border-b border-slate-50 last:border-none text-left"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onChange(a.code);
                setSearch(`${isEn ? a.cityEn : a.city} (${a.code})`);
                setIsOpen(false);
              }}
            >
              <div>
                <span className="font-bold text-slate-900">{isEn ? a.cityEn : a.city}</span>
                <span className="text-xs text-slate-400 block">{a.country}</span>
              </div>
              <span className="text-sm font-black text-sky-600 bg-sky-100 px-2 py-1 rounded-lg">{a.code}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function Evasion() {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'fr';
  const text = copy[lang];
  const isEn = lang === 'en';

  const [formData, setFormData] = useState({
    origin: '',
    destination: '',
    max_budget: '',
    travel_period: '',
    flexibility: 'flexible',
    passengers: '1',
    direct_only: false,
    email_enabled: false,
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleQuickFill = (deal: any) => {
    setFormData({
      ...formData,
      origin: deal.originCode,
      destination: deal.destCode,
      max_budget: deal.budget,
      travel_period: isEn ? deal.periodEn || deal.period : deal.period,
    });
    document.getElementById('evasion-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmitAlert = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.email_enabled) {
      setError(text.consentError);
      return;
    }

    setLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        sessionStorage.setItem('forfeo_evasion_pending_alert', JSON.stringify(formData));
        navigate(`/login?redirect=${encodeURIComponent(`${window.location.pathname}${window.location.search}`)}`);
        return;
      }

      const passengerNumber = formData.passengers === '4+' ? 4 : parseInt(formData.passengers, 10);
      const email = session.user.email;

      if (!email) {
        setError(text.serverError);
        return;
      }

      const sourceParams = new URLSearchParams(window.location.search);
      const source = sourceParams.get('source') || 'web';
      const response = await createSmartAlert({
        email,
        alert_type: 'flight',
        origin: formData.origin,
        destination: formData.destination,
        max_budget: formData.max_budget,
        travel_period: formData.travel_period,
        passengers: Number.isFinite(passengerNumber) ? passengerNumber : 1,
        direct_only: formData.direct_only,
        frequency: 'instant',
        language: lang,
        consent: formData.email_enabled,
        page_url: window.location.href,
        source,
        flexibility: formData.flexibility,
      } as any);

      if (!response.ok) {
        setError(response.details || response.error || text.saveError);
        setLoading(false);
        return;
      }

      sessionStorage.removeItem('forfeo_evasion_pending_alert');
      setSuccess(true);
    } catch (err) {
      setError(text.serverError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col font-sans selection:bg-sky-100 selection:text-sky-900">
      <Header />
      <main className="flex-grow pt-24 pb-20 overflow-hidden relative">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-sky-400/10 rounded-full blur-[120px] -z-10 animate-pulse" />
        <div className="absolute bottom-1/4 right-0 w-[400px] h-[400px] bg-indigo-400/10 rounded-full blur-[100px] -z-10" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <section className="text-center mb-20">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sky-50 border border-sky-100 text-sky-700 text-sm font-medium mb-6">
              <FaPlane className="animate-bounce" />
              <span>{text.badge}</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-extrabold text-slate-900 tracking-tight mb-6">
              {text.titleBefore} <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-indigo-600">{text.titleAccent}</span> {text.titleAfter}
            </h1>
            <p className="max-w-3xl mx-auto text-xl text-slate-600 leading-relaxed mb-10">{text.subtitle}</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button onClick={() => document.getElementById('evasion-form')?.scrollIntoView({ behavior: 'smooth' })} className="px-8 py-4 bg-slate-900 text-white rounded-2xl font-bold text-lg hover:bg-slate-800 transition-all hover:shadow-2xl hover:-translate-y-1 flex items-center gap-3 w-full sm:w-auto">
                {text.createAlert}<FaArrowRight className="text-sm opacity-70" />
              </button>
              <button onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })} className="px-8 py-4 bg-white text-slate-700 border border-slate-200 rounded-2xl font-bold text-lg hover:bg-slate-50 transition-all w-full sm:w-auto">
                {text.howItWorks}
              </button>
            </div>
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            <div id="evasion-form" className="lg:col-span-7">
              <div className="bg-white/80 backdrop-blur-xl border border-slate-100 rounded-[32px] p-8 md:p-10 shadow-2xl shadow-sky-900/5 relative">
                {success ? (
                  <div className="py-12 text-center animate-in fade-in zoom-in duration-500">
                    <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6"><FaCheckCircle size={40} /></div>
                    <h3 className="text-3xl font-bold text-slate-900 mb-4">{text.successTitle}</h3>
                    <p className="text-slate-600 text-lg mb-8">{text.successText}</p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-6">
                      <Link to="/mes-alertes-intelligentes" className="px-6 py-3 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-colors shadow-md w-full sm:w-auto">{text.viewAlerts}</Link>
                      <button onClick={() => setSuccess(false)} className="text-sky-600 font-bold hover:underline py-3 px-6 w-full sm:w-auto">{text.anotherAlert}</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <h2 className="text-2xl font-bold text-slate-900 mb-2">{text.formTitle}</h2>
                    <p className="text-slate-500 mb-8">{text.formSubtitle}</p>
                    <form onSubmit={handleSubmitAlert} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2 z-20">
                          <label className="text-sm font-semibold text-slate-700 ml-1">{text.origin}</label>
                          <AirportSelect required placeholder={text.originPlaceholder} value={formData.origin} onChange={(val: string) => setFormData({ ...formData, origin: val })} isEn={isEn} />
                        </div>
                        <div className="space-y-2 z-10">
                          <label className="text-sm font-semibold text-slate-700 ml-1">{text.destination}</label>
                          <AirportSelect required placeholder={text.destinationPlaceholder} value={formData.destination} onChange={(val: string) => setFormData({ ...formData, destination: val })} isEn={isEn} />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative z-0">
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-slate-700 ml-1">{text.maxBudget}</label>
                          <div className="relative">
                            <span className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 font-medium">$</span>
                            <input type="number" required placeholder={text.budgetPlaceholder} className="w-full pl-10 pr-5 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-sky-500 outline-none transition-all" value={formData.max_budget} onChange={(e) => setFormData({ ...formData, max_budget: e.target.value })} />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-slate-700 ml-1">{text.period}</label>
                          <input type="text" placeholder={text.periodPlaceholder} className="w-full px-5 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-sky-500 outline-none transition-all" value={formData.travel_period} onChange={(e) => setFormData({ ...formData, travel_period: e.target.value })} />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative z-0">
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-slate-700 ml-1">{text.flexibility}</label>
                          <select className="w-full px-5 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-sky-500 outline-none transition-all appearance-none" value={formData.flexibility} onChange={(e) => setFormData({ ...formData, flexibility: e.target.value })}>
                            <option value="fixed">{text.fixed}</option>
                            <option value="flexible">{text.flexible}</option>
                            <option value="very_flexible">{text.veryFlexible}</option>
                          </select>
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-slate-700 ml-1">{text.passengers}</label>
                          <select className="w-full px-5 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-sky-500 outline-none transition-all appearance-none" value={formData.passengers} onChange={(e) => setFormData({ ...formData, passengers: e.target.value })}>
                            <option value="1">1</option>
                            <option value="2">2</option>
                            <option value="3">3</option>
                            <option value="4+">4+</option>
                          </select>
                        </div>
                      </div>

                      <div className="flex flex-col gap-4 py-2 relative z-0">
                        <label className="flex items-center gap-3 cursor-pointer group">
                          <input type="checkbox" className="w-5 h-5 rounded border-slate-300 text-sky-600 focus:ring-sky-500 cursor-pointer" checked={formData.direct_only} onChange={(e) => setFormData({ ...formData, direct_only: e.target.checked })} />
                          <span className="text-slate-600 group-hover:text-slate-900 transition-colors">{text.directOnly}</span>
                        </label>
                        <label className="flex items-start gap-3 cursor-pointer group">
                          <input type="checkbox" required className="w-5 h-5 mt-0.5 rounded border-slate-300 text-sky-600 focus:ring-sky-500 cursor-pointer" checked={formData.email_enabled} onChange={(e) => setFormData({ ...formData, email_enabled: e.target.checked })} />
                          <span className="text-slate-600 text-sm leading-snug group-hover:text-slate-900 transition-colors">{text.emailConsent}</span>
                        </label>
                      </div>

                      {error && <div className="p-4 bg-red-50 text-red-600 rounded-xl flex items-center gap-3 text-sm font-medium border border-red-100 relative z-0"><FaInfoCircle /> {error}</div>}

                      <button type="submit" disabled={loading} className="w-full py-5 bg-gradient-to-r from-sky-600 to-indigo-600 text-white rounded-2xl font-bold text-xl hover:shadow-xl hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-3 relative z-0">
                        {loading ? text.loading : text.submit}{!loading && <FaBell className="text-sky-200" />}
                      </button>
                      <p className="text-center text-xs text-slate-400 mt-4 relative z-0">{text.disclaimer}</p>
                    </form>
                  </>
                )}
              </div>
            </div>

            <div className="lg:col-span-5 space-y-12">
              <div id="how-it-works">
                <h3 className="text-2xl font-bold text-slate-900 mb-8">{text.stepsTitle}</h3>
                <div className="space-y-8">
                  {[{ n: 1, title: text.step1Title, body: text.step1Text }, { n: 2, title: text.step2Title, body: text.step2Text }, { n: 3, title: text.step3Title, body: text.step3Text }].map((step) => (
                    <div className="flex gap-5" key={step.n}>
                      <div className="flex-shrink-0 w-12 h-12 bg-sky-100 text-sky-600 rounded-2xl flex items-center justify-center font-bold text-lg">{step.n}</div>
                      <div><h4 className="font-bold text-slate-900 mb-1">{step.title}</h4><p className="text-slate-500">{step.body}</p></div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-8 border-t border-slate-100">
                <h3 className="text-xl font-bold text-slate-900 mb-6">{text.popular}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {quickDeals.map((deal, idx) => (
                    <button key={idx} onClick={() => handleQuickFill(deal)} className="p-4 bg-slate-50 border border-slate-100 rounded-2xl text-left hover:bg-white hover:border-sky-200 hover:shadow-lg transition-all group">
                      <div className="flex justify-between items-start mb-2"><span className="text-xs font-bold text-sky-600 uppercase tracking-wider">{text.under} {deal.budget} $</span><FaSuitcaseRolling className="text-slate-300 group-hover:text-sky-400 transition-colors" /></div>
                      <div className="font-bold text-slate-900 leading-tight">{isEn ? deal.labelOEn : deal.labelO} → {isEn ? deal.labelDEn : deal.labelD}</div>
                      <div className="text-xs text-slate-500 mt-1">{text.planned} {isEn ? deal.periodEn : deal.period}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <section className="mt-32 py-20 bg-slate-950 rounded-[40px] text-white px-8 md:px-20 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-full h-full opacity-10 pointer-events-none"><FaGlobe size={600} className="absolute -top-40 -right-40 text-sky-400" /></div>
            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-4xl font-bold mb-6">{text.openTitle}</h2>
                <p className="text-slate-400 text-lg mb-10 leading-relaxed">{text.openText}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {openDestinations.map((envie, i) => (
                    <button key={i} onClick={() => handleQuickFill(envie)} className="flex items-center gap-3 bg-white/5 p-4 rounded-2xl border border-white/10 hover:bg-white/10 transition-colors text-left w-full cursor-pointer group">
                      <FaMapMarkedAlt className="text-sky-400 shrink-0 group-hover:scale-110 transition-transform" />
                      <span className="font-medium text-white">{isEn ? envie.labelEn : envie.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="hidden lg:block bg-gradient-to-br from-sky-500/20 to-indigo-500/20 backdrop-blur-2xl p-1 rounded-3xl border border-white/10">
                <div className="bg-slate-900 rounded-[22px] p-8">
                  <div className="flex items-center gap-4 mb-8"><div className="w-12 h-12 bg-sky-500 rounded-full flex items-center justify-center text-white font-bold">FE</div><div><div className="font-bold">{text.indexTitle}</div><div className="text-xs text-slate-500">{text.indexSub}</div></div></div>
                  <div className="space-y-6"><div className="h-2 bg-slate-800 rounded-full overflow-hidden"><div className="h-full bg-sky-500 w-[85%]"></div></div><div className="flex justify-between text-sm"><span className="text-slate-400">{text.priceScore}</span><span className="text-sky-400 font-bold">{text.excellent}</span></div><div className="p-4 bg-sky-500/10 rounded-xl border border-sky-500/20 text-sky-200 text-sm italic">“{text.sampleQuote}”</div></div>
                </div>
              </div>
            </div>
          </section>

          <section className="mt-32 max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">{text.emailPreviewTitle}</h2>
            <p className="text-slate-500 mb-12">{text.emailPreviewText}</p>
            <div className="bg-slate-50 rounded-[32px] p-2 md:p-4 border border-slate-100 shadow-sm overflow-hidden">
              <div className="bg-white rounded-2xl shadow-xl text-left overflow-hidden border border-slate-100">
                <div className="bg-slate-50 px-6 py-4 border-b border-slate-100 flex items-center justify-between"><div className="flex items-center gap-3"><div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center text-white text-[10px] font-bold tracking-tighter">FORFEO</div><span className="text-xs font-bold text-slate-500">ÉVASION</span></div><span className="text-[10px] text-slate-400">{isEn ? '2 min ago' : 'Il y a 2 min'}</span></div>
                <div className="p-8">
                  <div className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-2">{text.subject}</div>
                  <div className="text-lg font-bold text-slate-900 mb-6 italic">{text.subjectText}</div>
                  <div className="flex flex-col md:flex-row gap-8 items-center bg-sky-50/50 p-6 rounded-2xl border border-sky-100 mb-8"><div className="text-center md:text-left flex-grow"><div className="text-xs text-sky-600 font-bold mb-1">{text.spotted}</div><div className="text-2xl font-bold text-slate-900 mb-1 tracking-tight">Montréal (YUL) → Lisbonne (LIS)</div><div className="text-sm text-slate-500 flex items-center gap-2 justify-center md:justify-start"><FaRegClock /> {isEn ? 'Period: September 12 to 22' : 'Période : 12 au 22 Septembre'}</div></div><div className="text-center"><div className="text-3xl font-black text-sky-600">682 $</div><div className="text-[10px] text-slate-400">{text.detectedPrice}</div></div></div>
                  <a href={buildKiwiFlightSearchUrl({ origin: 'YUL', destination: 'LIS', travelPeriod: isEn ? 'September' : 'Septembre', passengers: 1, source: 'evasion_preview' })} target="_blank" rel="noopener noreferrer" className="w-full py-4 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-colors flex items-center justify-center gap-2 shadow-md hover:shadow-lg hover:-translate-y-0.5"><FaExternalLinkAlt /> {text.seeOffer}</a>
                </div>
              </div>
            </div>
          </section>

          <section className="mt-32 p-10 bg-slate-50 rounded-3xl border border-slate-100 text-slate-500 text-sm leading-relaxed">
            <h4 className="flex items-center gap-2 text-slate-900 font-bold mb-4 uppercase tracking-widest text-xs"><FaInfoCircle className="text-sky-500" /> {text.importantTitle}</h4>
            <p className="mb-4"><strong>Forfeo Évasion</strong> {text.importantText1.replace('Forfeo Évasion ', '')}</p>
            <p>{text.importantText2}</p>
          </section>

          <section className="mt-32 text-center pb-20">
            <h2 className="text-4xl font-bold text-slate-900 mb-8">{text.finalTitle}</h2>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <button onClick={() => document.getElementById('evasion-form')?.scrollIntoView({ behavior: 'smooth' })} className="px-10 py-5 bg-gradient-to-r from-sky-600 to-indigo-600 text-white rounded-2xl font-bold text-xl hover:shadow-2xl hover:scale-105 transition-all w-full sm:w-auto">{text.createAlert}</button>
              <Link to="/mes-alertes-intelligentes" className="text-slate-600 font-bold hover:text-sky-600 transition-colors flex items-center gap-2">{text.manageAlerts} <FaArrowRight size={14} /></Link>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
